import React, { useState } from "react";
import { SHLinkDescriptionDto } from "@docket/consumer-app-client-typescript-axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAtom } from "jotai";
import { errorLog } from "../../../utils/logger";
import { DocketAPIError, getAPIClient } from "../../../apiClient";
import { Modal } from "../../../components/modals/Modal";
import { ErrorModal } from "../../../components/modals/ErrorModal";
import {
  deleteSmartHealthLinkPasscode,
  updateSmartHealthLinkAtoms,
} from "../../../jotai/atomUpdater";
import { getDateTime } from "../../../utils/data";
import { shLinkPasscodeAtomFamily } from "../../../jotai/atoms";
import { Dialog } from "@capacitor/dialog";
import { Capacitor } from "@capacitor/core";

export default function ExistingLink({
  link,
  patient,
}: {
  link: SHLinkDescriptionDto;
  patient: string;
}) {
  const { t } = useTranslation();
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<string>();
  const [showPasscode, setShowPasscode] = useState(false);
  const [passcode] = useAtom(shLinkPasscodeAtomFamily(link.id.toString()));
  const [visiblePasscode, setVisiblePasscode] = useState<string>("******");
  const navigate = useNavigate();

  const deactivate = async () => {
    try {
      await getAPIClient().deleteSmartHealthLink(link.id.toString());
      await deleteSmartHealthLinkPasscode(link.id.toString());
      await updateSmartHealthLinkAtoms();
      navigate("/home/records");
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const onShowPasscodeClick = () => {
    if (!passcode) {
      if (Capacitor.isNativePlatform()) {
        Dialog.alert({
          title: "Passcode!",
          message:
            "Your passcode is not available - remember your passcode when accessing Docket® on a new device, or deactivate your SMART® Health Link and create a new one.",
        });
      } else {
        setShowErrorModal(
          "Your passcode is not available - remember your passcode when accessing Docket® on a new device, or deactivate your SMART® Health Link and create a new one."
        );
      }
    } else if (showPasscode) {
      setShowPasscode(false);
      setVisiblePasscode("******");
    } else {
      setVisiblePasscode(passcode.passcode);
      setShowPasscode(true);
    }
  };

  const onCopyClick = () => {
    navigator.clipboard.writeText(link.payload);
    if (Capacitor.isNativePlatform()) {
      Dialog.alert({
        title: "Copied",
        message:
          "SMART® Health Link copied to clipboard - send to your requesting party with the passcode.",
      });
    } else {
      setShowCopied(true);
    }
  };

  const onDeactivateClick = async () => {
    if (Capacitor.isNativePlatform()) {
      const result = await Dialog.confirm({
        title: "Deactivate Link?",
        message: "Are you sure you want to deactivate this SMART® Health Link?",
        // OK appears on the right on native devices, so we need to swap the button titles
        okButtonTitle: "No",
        cancelButtonTitle: "Yes",
      });

      if (!result.value) {
        await deactivate();
      }
    } else {
      setShowDeactivate(true);
    }
  };

  return (
    <div className="flex flex-col docket-gap-2">
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      <Modal
        id="search-nav-modal"
        open={showDeactivate}
        hasClose={true}
        title="Deactivate Link?"
        onCloseModal={() => setShowDeactivate(false)}
        actionButtons={
          <>
            <button
              className="btn docket-button"
              aria-label="Deactivate link"
              onClick={() => deactivate()}
              data-testid="deactivate-btn"
            >
              Yes
            </button>
            <button
              className="btn docket-button secondary"
              aria-label="Do not deactivate link"
              onClick={() => setShowDeactivate(false)}
            >
              No
            </button>
          </>
        }
      >
        <p>Are you sure you want to deactivate this SMART® Health Link?</p>
      </Modal>
      <Modal
        id="copied-modal"
        open={showCopied}
        hasClose={true}
        title="Copied"
        onCloseModal={() => setShowCopied(false)}
        actionButtons={
          <>
            <button
              className="btn docket-button"
              aria-label="OK"
              onClick={() => setShowCopied(false)}
              data-testid="ok-btn"
            >
              {t("generic.ok")}
            </button>
          </>
        }
      >
        <p>
          SMART® Health Link copied to clipboard - send to your requesting party with the passcode.
        </p>
      </Modal>
      <fieldset className="fieldset">
        <legend className="fieldset-legend mobile-text-color text-lg text-dark">
          {t("immunizations.shlink_existing_title")}
        </legend>
        <input
          type="text"
          value={link.payload}
          className="input input-neutral text-dark max-md:!outline-none max-md:!bg-white max-md:border-none max-md:w-full"
          readOnly
        />
        <p className="label italic mobile-text-color text-dark text-sm">
          Expires {getDateTime(link.expirationDate).toFormat("MM/dd/yyyy")}
        </p>
      </fieldset>
      <div className="flex docket-gap-1">
        <p className="mobile-text-color text-lg text-dark">Passcode:</p>
        <p
          data-show={showPasscode}
          className="mobile-text-color data-[show=false]:pt-1.5 data-[show=true]:pt-0.5 text-dark"
        >
          {visiblePasscode}
        </p>
        <div
          className="max-md:ml-auto cursor-pointer bg-dark-blue rounded-md w-[35px] h-[33px]"
          onClick={onShowPasscodeClick}
          data-testid="show-passcode"
        >
          <img
            width="20px"
            className="mx-auto my-2"
            src={showPasscode ? "/assets/images/eye.svg" : "/assets/images/eye-slash.svg"}
          />
        </div>
      </div>
      <div className="flex flex-col docket-gap-1">
        <p className="mobile-text-color text-lg text-dark">Share</p>
        <button
          className="btn docket-button-responsive md:w-fit max-md:w-full max-md:!py-1 max-md:text-lg"
          onClick={onCopyClick}
        >
          {t("immunizations.shlink_existing_copy_button")}
        </button>
      </div>
      <div className="flex flex-col docket-gap-1">
        <p className="mobile-text-color text-lg text-dark">Manage</p>
        <div className="flex max-md:flex-col docket-gap-1">
          <button
            className="btn docket-button-responsive md:w-fit max-md:w-full max-md:!py-1 max-md:text-lg"
            onClick={() =>
              navigate(`/home/records/${link.izHistoryUid}/smart-health-link/logs`, {
                state: { back: true, title: patient },
              })
            }
          >
            {t("immunizations.shlink_existing_access_log_button")}
          </button>
          <button
            className="btn docket-button-responsive md:w-fit max-md:w-full max-md:!text-red-500 max-md:!py-1 max-md:text-lg"
            onClick={onDeactivateClick}
            data-testid="confirm-deactivate-btn"
          >
            {t("immunizations.shlink_existing_deactivate_button")}
          </button>
        </div>
      </div>
    </div>
  );
}
